import { type ButtonHTMLAttributes, type ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'crew' | 'signal';
type Size = 'sm' | 'md' | 'lg' | 'crew';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  loading?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled,
  children,
  className = '',
  ...props
}: ButtonProps) {
  return (
    <button
      className={`btn btn-${variant} btn-${size} ${fullWidth ? 'btn-full' : ''} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <span className="btn-spinner" /> : children}
      <style>{`
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-sm);
          border: none;
          border-radius: var(--radius-md);
          font-family: var(--font-body);
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s, transform 0.1s, border-color 0.15s;
          white-space: nowrap;
        }
        .btn:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
        .btn:not(:disabled):active {
          transform: scale(0.98);
        }
        .btn-sm { padding: var(--space-xs) var(--space-md); font-size: 0.8125rem; }
        .btn-md { padding: var(--space-sm) var(--space-lg); font-size: 0.9375rem; }
        .btn-lg { padding: var(--space-md) var(--space-xl); font-size: 1rem; }
        .btn-crew {
          padding: var(--space-lg) var(--space-xl);
          font-size: 1.25rem;
          min-height: var(--touch-min);
          border-radius: var(--radius-lg);
        }
        .btn-full {
          width: 100%;
          max-width: 100%;
          white-space: normal;
          text-wrap: balance;
        }
        .btn-primary {
          background: var(--color-ink);
          color: var(--color-white);
        }
        .btn-primary:not(:disabled):hover {
          background: var(--color-graphite);
        }
        .btn-signal {
          background: var(--color-signal);
          color: var(--color-ink);
        }
        .btn-signal:not(:disabled):hover {
          filter: brightness(0.95);
        }
        .btn-secondary {
          background: var(--color-fog);
          color: var(--color-ink);
          border: 1px solid var(--color-border);
        }
        .btn-secondary:not(:disabled):hover {
          background: #E4E8EC;
        }
        .btn-ghost {
          background: transparent;
          color: var(--color-ink);
        }
        .btn-ghost:not(:disabled):hover {
          background: var(--color-fog);
        }
        .btn-danger {
          background: var(--color-urgent);
          color: white;
        }
        .btn-danger:not(:disabled):hover {
          background: #A93226;
        }
        .btn-crew {
          background: var(--color-signal);
          color: var(--color-ink);
        }
        .btn-crew:not(:disabled):hover {
          filter: brightness(0.95);
        }
        .btn-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid currentColor;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }
      `}</style>
    </button>
  );
}
